import type { Colors } from '@factorialco/deprecated-design-system'
import { PlanningBalancesByPeriodsQuery, PlanningBalancesQuery } from 'generated/resources/graphql'

import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { ShiftsRow } from 'modules/shift_management/lib/types'

import overPlannedHoursCalculator from './overPlannedHoursCalculator'
import usePlannedHoursTooltip from './usePlannedHoursTooltip'

type TimePlanningBalance = NonNullable<
  PlanningBalancesQuery['timePlanning']['balancesConnection']['nodes']
>[number]
type TimePlanningBalancesByPeriod = NonNullable<
  PlanningBalancesByPeriodsQuery['timePlanning']['balancesByPeriodsConnection']['nodes']
>[number]

const usePlannedHours = (
  shiftsRow: ShiftsRow,
  balances?: TimePlanningBalance,
  periodBalances?: TimePlanningBalancesByPeriod[]
) => {
  const [{ view }] = useSchedule()

  const totalDuration = shiftsRow.hoursPerRangeAsMilliseconds(view.firstDay, view.lastDay)

  const { contractHours = 0, leaveHours = 0, bankHolidayHours = 0 } = balances ?? {}
  const totalHours = (contractHours ?? 0) - (leaveHours ?? 0) - (bankHolidayHours ?? 0)
  const totalHoursInMilliseconds = totalHours * 60 * 60 * 1000

  const { overPlannedPeriods, maximumWeeklyHours } = overPlannedHoursCalculator(
    shiftsRow,
    periodBalances ?? [],
    shiftsRow.maximumWeeklyHours
  )

  const isOverMaximum = overPlannedPeriods.length > 0
  const hasPlannedHours = totalDuration > 0
  const noHoursPlanned = totalDuration === 0
  const allHoursPlanned = totalDuration >= totalHoursInMilliseconds

  const color: Colors = (() => {
    if (isOverMaximum) return 'radicalRed1000'
    if (allHoursPlanned) return 'lime1000'
    if (hasPlannedHours) return 'tangerine1000'
    if (noHoursPlanned) return 'tangerine200'
    return 'tangerine200'
  })()

  const { plannedHoursTooltipCopy } = usePlannedHoursTooltip({
    isOverMaximum,
    noHoursPlanned,
    allHoursPlanned,
    totalDuration,
    overPlannedPeriods,
    maximumWeeklyHours,
    leaveHours: leaveHours ?? 0,
    bankHolidayHours: bankHolidayHours ?? 0,
  })

  return {
    color,
    plannedHoursTooltipCopy,
    isOverMaximum,
    hasPlannedHours,
    totalDuration,
  }
}

export default usePlannedHours
